import React from 'react';
import { MatterDetailed } from '../../../types/matters';
import { highlightText } from '../../../utils/textHighlight';

interface MatterCardProps {
  matter: MatterDetailed;
  onClick: () => void;
  searchQuery?: string; // Add searchQuery prop
}

const MatterCard: React.FC<MatterCardProps> = ({ matter, onClick, searchQuery = '' }) => {
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-200';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-200';
      case 'on_hold':
        return 'bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-200';
      case 'closed':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-200';
    }
  };

  const getBillingMethodIcon = (method: string) => {
    switch (method) {
      case 'Hourly': return '⏰';
      case 'Flat': return '📋';
      case 'Contingency': return '🤝';
      default: return '💰';
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <div
      onClick={onClick}
      className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 hover:shadow-md hover:border-blue-300 dark:hover:border-blue-600 transition-all duration-200 cursor-pointer"
    >
      {/* Header */}
      <div className="mb-4">
        {/* Top row with matter number and status */}
        <div className="flex items-center justify-between mb-2">
          <div className="text-sm font-mono text-blue-600 dark:text-blue-400 font-medium">
            {highlightText(matter.displayNumber, searchQuery)}
          </div>
          <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(matter.status || 'pending')}`}>
            {matter.status ? matter.status.charAt(0).toUpperCase() + matter.status.slice(1).replace('_', ' ') : 'Pending'}
          </span>
        </div>
        
        {/* Matter title on its own line */}
        <h3 className="font-semibold text-gray-900 dark:text-white text-base leading-tight break-words line-clamp-2">
          {highlightText(matter.title, searchQuery)}
        </h3>
      </div>

      {/* Matter Info */}
      <div className="space-y-2 mb-4">
        {/* Client */}
        <div className="flex items-center text-sm text-gray-600 dark:text-gray-300">
          <span className="mr-2">👤</span>
          <span className="truncate font-medium">
            {matter.client?.name ? highlightText(matter.client.name, searchQuery) : '-'}
          </span>
        </div>
        
        {/* Practice Area */}
        <div className="flex items-center text-sm text-gray-600 dark:text-gray-300">
          <span className="mr-2">⚖️</span>
          <span className="truncate">{highlightText(matter.practice_area, searchQuery)}</span>
        </div>

        {/* Responsible Attorney */}
        <div className="flex items-center text-sm text-gray-600 dark:text-gray-300">
          <span className="mr-2">🧑‍💼</span>
          <span className="truncate">
            {matter.responsibleAttorney?.name ? highlightText(matter.responsibleAttorney.name, searchQuery) : '-'}
          </span>
        </div>

        {/* Billing Method */}
        <div className="flex items-center text-sm text-gray-600 dark:text-gray-300">
          <span className="mr-2">{getBillingMethodIcon(matter.billingMethod)}</span>
          <span>{matter.billingMethod}</span>
        </div>
      </div>

      {/* Financial Summary */}
      <div className="border-t border-gray-200 dark:border-gray-700 pt-3 mt-3">
        <div className="grid grid-cols-2 gap-3 text-xs">
          <div>
            <div className="text-gray-500 dark:text-gray-400 mb-1">Estimated Value</div>
            <div className="font-semibold text-gray-900 dark:text-white">
              {matter.estimated_value ? formatCurrency(matter.estimated_value) : '—'}
            </div>
          </div>
          <div>
            <div className="text-gray-500 dark:text-gray-400 mb-1">WIP</div>
            <div className="font-semibold text-gray-900 dark:text-white">
              {formatCurrency(matter.workInProgress)}
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="border-t border-gray-200 dark:border-gray-700 pt-3 mt-3">
        <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
          <span>Opened: {formatDate(matter.openDate)}</span>
          <div className="flex items-center space-x-3">
            {matter.notes?.length > 0 && (
              <span className="flex items-center">
                <span className="mr-1">📝</span>
                {matter.notes.length}
              </span>
            )}
            {matter.documents?.length > 0 && (
              <span className="flex items-center">
                <span className="mr-1">📎</span>
                {matter.documents.length}
              </span>
            )}
            {matter.tasks?.length > 0 && (
              <span className="flex items-center">
                <span className="mr-1">✓</span>
                {matter.tasks.filter(t => t.status === 'completed').length}/{matter.tasks.length}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MatterCard;
